import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Chip,
  Stack,
  Paper,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateCharacter, removeCharacter } from './charactersSlice';
import useThemeStyles from '../../hooks/useThemeStyles';
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import characterService from '../../utils/apiRequests/characterService';

const CharacterDetailsPanel = ({ character, onClose, onDelete }) => {
  const { colors } = useThemeStyles();
  const { projectId } = useParams();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    fullName: '',
    displayName: '',
    age: '',
    race: '',
    hairColor: '',
    eyeColor: '',
    sexuality: '',
    personality: '',
    notes: '',
    tags: [],
  });

  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const sexualityOptions = [
    { value: '', label: 'Not specified' },
    { value: 'Heterosexual', label: 'Heterosexual' },
    { value: 'Homosexual', label: 'Homosexual' },
    { value: 'Bisexual', label: 'Bisexual' },
    { value: 'Pansexual', label: 'Pansexual' },
    { value: 'Asexual', label: 'Asexual' },
    { value: 'Demisexual', label: 'Demisexual' },
    { value: 'Queer', label: 'Queer' },
    { value: 'Other', label: 'Other' },
  ];

  useEffect(() => {
    if (character) {
      setFormData({
        fullName: character.fullName || character.displayName || '',
        displayName: character.displayName || character.fullName || '',
        age: character.age || '',
        race: character.race || '',
        hairColor: character.hairColor || '',
        eyeColor: character.eyeColor || '',
        sexuality: character.sexuality || '',
        personality: character.personality || '',
        notes: character.notes || '',
        tags: character.tags || [],
      });
      setErrors({});
    }
  }, [character]);

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-update displayName if fullName changes
    if (field === 'fullName') {
      setFormData((prev) => ({
        ...prev,
        displayName: value.trim() || 'Unnamed Character',
      }));
    }
  };

  const handleSave = async () => {
    if (!character || !projectId) return;

    // Validate required fields
    const newErrors = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      const updateData = {
        fullName: formData.fullName.trim(),
        displayName: formData.displayName.trim(),
        age: formData.age ? parseInt(formData.age, 10) : null,
        race: formData.race || '',
        hairColor: formData.hairColor || '',
        eyeColor: formData.eyeColor || '',
        sexuality: formData.sexuality || '',
        personality: formData.personality || '',
        notes: formData.notes || '',
        tags: formData.tags || [],
      };

      const response = await characterService.updateCharacter(projectId, character.id, updateData);

      if (response.error) {
        setErrors({ submit: 'Failed to save character. Please try again.' });
        setSaving(false);
        return;
      }

      // Normalize and update Redux
      const normalizedCharacter = {
        ...response.data.data,
        id: response.data.data.id || response.data.data._id?.toString() || response.data.data._id,
        projectId: response.data.data.projectId?.toString() || response.data.data.projectId,
        familyId: response.data.data.familyId?.toString() || response.data.data.familyId || null,
        createdAt: response.data.data.createdAt
          ? new Date(response.data.data.createdAt).getTime()
          : character.createdAt,
        updatedAt: response.data.data.updatedAt
          ? new Date(response.data.data.updatedAt).getTime()
          : Date.now(),
      };

      dispatch(updateCharacter({ id: character.id, updates: normalizedCharacter }));

      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error updating character:', err);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!character || !projectId) return;

    try {
      const response = await characterService.deleteCharacter(projectId, character.id);

      if (response.error) {
        console.error('Error deleting character:', response.error);
        setDeleteDialogOpen(false);
        return;
      }

      // Remove from Redux
      dispatch(removeCharacter(character.id));
      setDeleteDialogOpen(false);

      if (onDelete) {
        onDelete();
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error deleting character:', err);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  if (!character) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: '400px',
        maxWidth: '90vw',
        backgroundColor: colors.background.primary,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1200,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: 2,
          borderBottom: `1px solid ${colors.border.subtle}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Character Details
        </Typography>
        <Box>
          <IconButton size="small" onClick={handleDeleteClick} color="error">
            <DeleteIcon />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: 2,
        }}
      >
        <Stack spacing={2}>
          {/* Name Fields */}
          <TextField
            label="Full Name"
            value={formData.fullName}
            onChange={handleChange('fullName')}
            fullWidth
            size="small"
            required
            error={!!errors.fullName}
            helperText={errors.fullName}
          />
          <TextField
            label="Display Name"
            value={formData.displayName}
            onChange={handleChange('displayName')}
            fullWidth
            size="small"
            helperText="Auto-generated from full name, but can be customized"
          />

          <Divider />

          {/* Basic Info */}
          <TextField
            label="Age"
            type="number"
            value={formData.age}
            onChange={handleChange('age')}
            fullWidth
            size="small"
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Race / Ethnicity"
            value={formData.race}
            onChange={handleChange('race')}
            fullWidth
            size="small"
          />
          <TextField
            label="Hair Color"
            value={formData.hairColor}
            onChange={handleChange('hairColor')}
            fullWidth
            size="small"
          />
          <TextField
            label="Eye Color"
            value={formData.eyeColor}
            onChange={handleChange('eyeColor')}
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Sexuality</InputLabel>
            <Select
              value={formData.sexuality}
              onChange={handleChange('sexuality')}
              label="Sexuality"
            >
              {sexualityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          {/* Personality */}
          <TextField
            label="Personality"
            value={formData.personality}
            onChange={handleChange('personality')}
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="Short summary of personality traits..."
          />

          {/* Tags */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {formData.tags.map((tag) => (
                <Chip key={tag} label={tag} onDelete={() => handleRemoveTag(tag)} size="small" />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddTag();
                  }
                }}
                placeholder="Add tag..."
                size="small"
                fullWidth
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddTag}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* Notes */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Notes
            </Typography>
            <TextField
              value={formData.notes}
              onChange={handleChange('notes')}
              fullWidth
              multiline
              rows={8}
              size="small"
              placeholder="Free-form notes about this character (supports markdown)..."
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          padding: 2,
          borderTop: `1px solid ${colors.border.subtle}`,
        }}
      >
        <Button
          variant="contained"
          fullWidth
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <DeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName={character?.displayName || character?.fullName || 'this character'}
      />
    </Paper>
  );
};

CharacterDetailsPanel.propTypes = {
  character: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    displayName: PropTypes.string,
    age: PropTypes.number,
    race: PropTypes.string,
    hairColor: PropTypes.string,
    eyeColor: PropTypes.string,
    sexuality: PropTypes.string,
    personality: PropTypes.string,
    notes: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    createdAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

CharacterDetailsPanel.defaultProps = {
  character: null,
  onDelete: null,
};

export default CharacterDetailsPanel;
